// function transformCSPageEntries(options) {
//   const { response } = options || {}
//   const transformedZones = {}

import { APPConfig } from 'config/appConfig'
import { application, enableNewFeature } from 'src/utils'
import { getMysiteSponsor, isEquinoxMysite } from 'src/utils/mySiteUtils'

/**
 * Check if entry URL matches the current URL pattern
 * @param {string} entryUrl - The entry URL pattern (may contain placeholders like <cftId>)
 * @param {string} currentUrl - The current URL being accessed
 * @returns {boolean} - True if the entry matches the current URL
 */
const doesUrlMatch = (entryUrl, currentUrl) => {
  if (!entryUrl || !currentUrl) return false

  const cleanEntryUrl = entryUrl.replace(/^\/+|\/+$/g, '')
  const cleanCurrentUrl = currentUrl.replace(/^\/+|\/+$/g, '')

  if (/<[^>]+>/.test(cleanEntryUrl)) {
    const regexPattern = cleanEntryUrl.replace(/<[^>]+>/g, '[^/]+')
    const regex = new RegExp(`^${regexPattern}$`)
    return regex.test(cleanCurrentUrl)
  }

  return cleanEntryUrl === cleanCurrentUrl
}

//   if (response?.entries?.[0]?.sf_zones?.length > 0) {
//     const sfZones = response?.entries?.[0]?.sf_zones

//     sfZones.forEach(zone => {
//       const sfZone = zone?.sf_zone
//       if (sfZone) {
//         const zoneName = sfZone.zone_name
//         const components = sfZone.sf_components_block
//         const zoneComponents = components.map(component => {
//           if (component?.ns_hero) {
//             return { hero: transformHeroBlock({ component }) }
//           }
//         })
//         transformedZones[zoneName] = zoneComponents
//       }
//     })
//   }

//   return transformedZones
// }

function transformCSPageEntries(options) {
  const { response, currentUrl } = options || {}
  const transformedZones = {}

  if (!response?.entries?.length) {
    return transformedZones
  }

  // Process zones from an entry and add them to the transformedZones object
  const processEntryZones = entry => {
    if (!entry?.sf_zones?.length) return

    entry.sf_zones?.forEach(zone => {
      const sfZone = zone?.sf_zone
      if (!sfZone) return

      const zoneName = sfZone?.zone_name
      const components = sfZone?.sf_components_block || []

      const zoneComponents = components
        .map(component => {
          if (component?.ns_hero && component?.ns_hero?.show) {
            return { hero: transformHeroBlock({ component }) }
          }
          return null
        })
        .filter(Boolean)

      // Only add/override if there are components
      if (zoneComponents.length > 0) {
        transformedZones[zoneName] = zoneComponents
      }
    })
  }

  const processMicroFrontendConfigs = entry => {
    if (!entry?.micro_frontend_configs?.length) return

    const microFrontendComponents = entry.micro_frontend_configs
      .map(config => {
        if (config?.micro_frontend && config?.micro_frontend?.show) {
          return { microFrontend: transformMicroFrontendBlock({ config }) }
        }
        return null
      })
      .filter(Boolean)
    if (microFrontendComponents.length > 0) {
      transformedZones.microFrontend = microFrontendComponents
    }
  }

  let defaultEntry = null
  let actualEntry = null

  for (const entry of response?.entries || []) {
    if (entry?.url === '/default') {
      defaultEntry = entry
    } else if (!actualEntry) {
      actualEntry = entry
    }
    if (defaultEntry && actualEntry) {
      break
    }
  }

  const hasActualEntry = !!(
    actualEntry && doesUrlMatch(actualEntry?.url, currentUrl)
  )

  if (defaultEntry) {
    processEntryZones(defaultEntry)
    // Always include default microFrontend configs — the MFE's own
    // getServerSideProps decides whether the page exists or not.
    processMicroFrontendConfigs(defaultEntry)
  }

  if (hasActualEntry) {
    processEntryZones(actualEntry)
    processMicroFrontendConfigs(actualEntry)
  }

  // Attach as a non-enumerable flag so consumers can check it without it
  // polluting Object.keys() / zone-count checks in sendPageResponse.
  Object.defineProperty(transformedZones, 'hasActualEntry', {
    value: hasActualEntry,
    enumerable: false,
    writable: true,
    configurable: true,
  })

  return transformedZones
}

function transformHeroBlock(options) {
  const { component } = options || {}

  const transformedJson = {}
  transformedJson.title = component?.ns_hero?.title
  transformedJson.description = component?.ns_hero?.description
  transformedJson.image = component?.ns_hero?.image

  transformedJson.background_color = ''
  transformedJson.main_gradient = ''
  transformedJson.sub_gradient = ''
  transformedJson.main_font_color = ''
  transformedJson.sub_font_color = ''
  transformedJson.hero = []

  if (component?.ns_hero?.hero) {
    const banners = component?.ns_hero?.hero?.map(hero => {
      return transformHeroBanner({ hero })
    })
    transformedJson.hero = banners
  }

  return transformedJson
}

function transformMicroFrontendBlock(options) {
  const { config } = options || {}
  const microFrontend = config?.micro_frontend || {}

  const transformedMicroFrontend = {
    show: microFrontend?.show || false,
    enableServerRendering: microFrontend?.enable_server_rendering || false,
    fullWidth: microFrontend?.full_width || false,
    containerBackgroundColor: microFrontend?.container_background_color || '{}',
    includeGap: microFrontend?.include_gap || false,
    addPadding: microFrontend?.add_padding || false,
    microfrontendApp: microFrontend?.microfrontend_app || '',
    microFrontendComponent: microFrontend?.micro_frontend_component || '',
  }

  return transformedMicroFrontend
}

function transformHeroBanner(options) {
  const { hero } = options || {}
  const heroBanner = hero?.hero_banner
  let callToAction = {}
  let mediaProperties = {}
  try {
    callToAction = JSON.parse(heroBanner?.call_to_action)
    mediaProperties = JSON.parse(heroBanner?.image)
  } catch (e) {
    console.warn('error in transformHeroBanners', e)
  }

  const imageProperties = mediaProperties?.image?.[0]
  let baseImageUrl = ''

  let imagePropertyFiles = imageProperties?.files || {}
  if (imageProperties?.type?.toLowerCase() === 'video') {
    imagePropertyFiles = imageProperties?.files?.[0]
    baseImageUrl = imagePropertyFiles?.[0]?.webImage?.url
  }

  const transformedBanners = {
    hero_block: {
      heading: heroBanner?.header,
      background_image: '',
      image_description: heroBanner?.description,
      big_title: heroBanner?.header,
      description: heroBanner?.description,
      type: imageProperties?.type,
      video: {
        url: imagePropertyFiles?.videoUrls?.[0],
        poster: imagePropertyFiles?.selected?.url || baseImageUrl,
      },
      equinox_link: {
        title: callToAction?.text,
        href: callToAction?.url,
      },
      bynder_background_image: [
        {
          id: '',
          name: imageProperties?.description || '',
          type: 'IMAGE',
          url: imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
          derivatives: {
            thumbnail: imagePropertyFiles?.thumbnail?.url || baseImageUrl,
            webImage: imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            mini: imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            PDP_Carousel_Image:
              imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            transformBaseUrl:
              imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            small: imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            'Web Image':
              imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
            thumbnail: imagePropertyFiles?.thumbnail?.url || baseImageUrl,
            Large: imagePropertyFiles?.transformBaseUrl?.url || baseImageUrl,
          },
        },
      ],
    },
  }

  return transformedBanners
}

/**
 * Helper function to replace URL placeholders with actual language and country values
 * @param {string} url - URL with placeholders like {language}, {country}, {COUNTRY}
 * @param {string} language - The language code (e.g., 'en')
 * @param {string} country - The country code (e.g., 'us')
 * @returns {string} URL with placeholders replaced
 */
const replaceUrlPlaceholders = (url, language = 'en', country = 'us') => {
  if (!url || typeof url !== 'string') return url

  // window is absent during SSR; ServerApp records the request origin instead.
  const origin =
    typeof window !== 'undefined'
      ? window?.location?.origin
      : application.requestDomain
  const domain = origin?.split('//')[1]

  let decodedUrl = url
    .replace(/%7Blanguage%7D/g, '{language}')
    .replace(/%7BCOUNTRY%7D/g, '{COUNTRY}')
    .replace(/%7Bcountry%7D/g, '{country}')
    .replace(/%7Bdomain%7D/g, '{domain}')
  let processedUrl = decodedUrl
    .replace(/{language}/g, language)
    .replace(/{COUNTRY}/g, country.toUpperCase())
    .replace(/{country}/g, country.toLowerCase())
    .replace(/{domain}/g, domain)

  const countryLower = country.toLowerCase()
  let finalUrl = processedUrl

  const pathSegment = `/${countryLower}/${language}`
  const duplicateSegment = `${pathSegment}${pathSegment}`

  while (finalUrl.includes(duplicateSegment)) {
    finalUrl = finalUrl.replace(duplicateSegment, pathSegment)
  }

  if (
    finalUrl.includes('/catalog/') &&
    finalUrl.startsWith(`/${countryLower}/${language}/`)
  ) {
    const relativePath = finalUrl.replace(`/${countryLower}/${language}`, '')
    finalUrl = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
  }
  return finalUrl
}

/**
 * Transform subcategories from ContentStack format to application format
 * @param {Array} subcategories - Array of subcategory objects from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @returns {Array} Transformed subcategories
 */
const transformSubCategories = (
  subcategories,
  language = 'en',
  country = 'us'
) => {
  if (!Array.isArray(subcategories)) {
    return []
  }

  const result = subcategories
    ?.filter(
      sub => sub && !sub?.is_hidden_from_navigation && sub?.status === 'Active'
    )
    ?.map(subCategory => {
      const categoryPath =
        subCategory?.category_id ||
        subCategory?.seo_information?.slug ||
        subCategory?.display_name?.replace(/\s+/g, '_')?.toLowerCase()

      const categoryUrl = `/catalog/${categoryPath}`

      const transformed = {
        label: subCategory?.display_name || '',
        identifier: subCategory?.category_id || '',
        href: categoryUrl,
        items: [],
      }

      if (
        subCategory?.subcategories &&
        Array.isArray(subCategory.subcategories)
      ) {
        const activeSubcategories = subCategory.subcategories?.filter(
          sub =>
            sub && !sub?.is_hidden_from_navigation && sub?.status === 'Active'
        )

        transformed.items = activeSubcategories
          ?.map(sub => {
            const subCategoryPath =
              sub?.category_id ||
              sub?.seo_information?.slug ||
              sub?.display_name?.replace(/\s+/g, '_')?.toLowerCase()

            let subCategoryUrl = `/catalog/${subCategoryPath}`
            const mysiteDomain = getMysiteSponsor()
            return {
              title: sub?.display_name || '',
              href: subCategoryUrl,
            }
          })
          ?.filter(Boolean)
      }

      return transformed
    })
    ?.filter(sub => sub?.label)

  return result || []
}

/**
 * Transform a ContentStack category item to application format
 * @param {Object} categoryData - Category data from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @returns {Object|null} Transformed category or null if invalid
 */
const transformCategory = (categoryData, language = 'en', country = 'us') => {
  if (!categoryData?.display_name) return null

  const categoryPath =
    categoryData?.category_id ||
    categoryData?.seo_information?.slug ||
    categoryData?.display_name?.replace(/\s+/g, '_')?.toLowerCase()

  const categoryUrl = `/catalog/${categoryPath}`

  const transformedCategory = {
    label: categoryData.display_name,
    identifier:
      categoryData.uid ||
      categoryData.display_name?.replace(/\s+/g, '_')?.toLowerCase(),
    href: categoryUrl,
    subCategories: [],
  }

  if (
    categoryData?.subcategories &&
    Array.isArray(categoryData.subcategories)
  ) {
    transformedCategory.subCategories = transformSubCategories(
      categoryData.subcategories,
      language,
      country
    )
  }

  transformedCategory.isLink = transformedCategory.subCategories.length === 0

  return transformedCategory
}

/**
 * Transform a ContentStack link item to application format
 * @param {Object} linkData - Link data from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @returns {Object|null} Transformed link category or null if invalid
 */
const transformLink = (linkData, language = 'en', country = 'us') => {
  if (!linkData?.title) return null

  const originalHref = linkData.href || ''
  const processedHref = replaceUrlPlaceholders(originalHref, language, country)

  if (linkData.title?.toLowerCase() === 'brand') {
    console.log('Brand category URL processing:', {
      title: linkData.title,
      originalHref,
      processedHref,
    })
  }

  return {
    label: linkData.title,
    href: processedHref,
    isLink: true,
    subCategories: [],
  }
}

/**
 * Transform ContentStack lists_of_links to application format
 * @param {Object} listsData - Lists data from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @returns {Object|null} Transformed category or null if invalid
 */
const transformListsOfLinks = (listsData, language = 'en', country = 'us') => {
  if (!listsData?.title) return null

  const transformedCategory = {
    label: listsData.title,
    identifier: listsData.title?.replace(/\s+/g, '_')?.toLowerCase() || '',
    subCategories: [],
  }

  if (listsData?.link_list && Array.isArray(listsData?.link_list)) {
    listsData?.link_list?.forEach(linkGroup => {
      const subCategory = {
        label: linkGroup?.title || '',
        items: [],
      }

      if (linkGroup?.link && Array.isArray(linkGroup?.link)) {
        subCategory.items = linkGroup?.link
          ?.filter(link => link?.title)
          ?.map(link => ({
            title: link?.title,
            href: replaceUrlPlaceholders(link?.href, language, country) || '',
          }))
      }

      if (subCategory?.items?.length > 0) {
        transformedCategory?.subCategories?.push(subCategory)
      }
    })
  }

  return transformedCategory?.subCategories?.length > 0
    ? transformedCategory
    : null
}

/**
 * Process a top-level navigation item from ContentStack
 * @param {Object} topNavItem - Navigation item from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @param {string} jumpToLabel - Jump to label text
 * @returns {Object|null} Transformed category or null
 */
const processTopNavItem = (topNavItem, language, country, jumpToLabel = '') => {
  if (
    topNavItem?.category?.category &&
    Array.isArray(topNavItem?.category?.category)
  ) {
    const categoryData = topNavItem?.category?.category?.[0]
    if (categoryData?.status?.toLowerCase() !== 'active') {
      return null
    }
    const transformedCategory = transformCategory(
      categoryData,
      language,
      country
    )

    if (
      transformedCategory &&
      topNavItem?.category?.additional_links &&
      Array.isArray(topNavItem?.category?.additional_links)
    ) {
      const jumpToItems = topNavItem?.category?.additional_links
        ?.filter(link => link?.title && link?.href)
        ?.map((link, index) => {
          const processedHref = replaceUrlPlaceholders(
            link.href,
            language,
            country
          )

          return {
            title: link?.title,
            href: processedHref,
          }
        })

      if (jumpToItems?.length > 0 && jumpToLabel) {
        transformedCategory?.subCategories?.push({
          label: jumpToLabel,
          items: jumpToItems,
        })
      }
    }

    return transformedCategory
  } else if (topNavItem?.link?.link) {
    const linkData = topNavItem?.link?.link
    return linkData ? transformLink(linkData, language, country) : null
  } else if (topNavItem?.lists_of_links) {
    return transformListsOfLinks(topNavItem?.lists_of_links, language, country)
  }
  return null
}

/**
 * Process a navigation item from ContentStack
 * @param {Object} navItem - Navigation item from ContentStack
 * @param {string} language - Language code
 * @param {string} country - Country code
 * @returns {Object|null} Transformed category or null
 */
const processNavItem = (navItem, language, country) => {
  if (navItem?.category?.category?.[0]) {
    const categoryData = navItem?.category?.category?.[0]

    const categoryPath =
      categoryData?.category_id ||
      categoryData?.seo_information?.slug ||
      categoryData?.display_name?.replace(/\s+/g, '_')?.toLowerCase()

    const categoryUrl = `/catalog/${categoryPath}`

    const subCategories = categoryData?.subcategories
      ? transformSubCategories(categoryData?.subcategories, language, country)
      : []

    return {
      label: categoryData?.title || categoryData?.display_name || '',
      identifier: categoryData?.category_id || '',
      href: categoryUrl,
      subCategories,
      isLink: subCategories?.length === 0,
    }
  } else if (navItem?.link?.link) {
    const linkData = navItem?.link?.link
    return transformLink(linkData, language, country)
  } else if (navItem?.lists_of_links) {
    return transformListsOfLinks(navItem?.lists_of_links, language, country)
  }
  return null
}

/**
 * Transform ContentStack API navigation data to application MenuData format
 * @param {Object} contentStackData - The raw data from ContentStack API
 * @param {string} language - The language code (e.g., 'en')
 * @param {string} country - The country code (e.g., 'us')
 * @returns {Object} Transformed data in the expected format
 */
const transformContentStackToMenuData = (
  contentStackData,
  language = 'en',
  country = 'us'
) => {
  try {
    const topNavigation = contentStackData?.top_navigation || []
    const transformedCategories = []
    let jumpToLabel = ''
    let isLegacyURLOpenInSameTab = false
    let legacyURLList = []
    topNavigation?.forEach(navItem => {
      if (navItem?.reference && Array.isArray(navItem?.reference)) {
        navItem?.reference?.forEach(refItem => {
          if (refItem?.jump_to) {
            jumpToLabel = refItem?.jump_to + '##jump'
          }
          if (refItem?.is_legacy_url_open_in_same_tab) {
            isLegacyURLOpenInSameTab = refItem?.is_legacy_url_open_in_same_tab
          }
          if (refItem?.legacy_list) {
            legacyURLList = refItem?.legacy_list
          }
          if (
            refItem?.top_navigation &&
            Array.isArray(refItem?.top_navigation)
          ) {
            refItem?.top_navigation?.forEach(topNavItem => {
              const enableTopNavItemsRestriction = enableNewFeature(
                'enableTopNavItemsRestriction'
              )
              const showTopNavItemsForNewUI =
                APPConfig?.getAppConfig?.()?.showTopNavItemsForNewUI || 6
              const shouldInclude =
                !enableTopNavItemsRestriction ||
                transformedCategories?.length < showTopNavItemsForNewUI

              if (shouldInclude) {
                setTransFormedCategories(
                  topNavItem,
                  language,
                  country,
                  jumpToLabel,
                  transformedCategories
                )
              }
            })
          }
        })
      } else {
        const category = processNavItem(navItem, language, country)
        if (category) {
          transformedCategories?.push(category)
        }
      }
    })

    const result = {
      categories: transformedCategories,
      jumpToLabel,
      isLegacyURLOpenInSameTab,
      legacyURLList,
    }
    return result
  } catch (error) {
    console.error('Error transforming ContentStack data:', error)
    return {
      categories: [],
      jumpToLabel: '',
    }
  }
}

const setTransFormedCategories = (
  topNavItem,
  language,
  country,
  jumpToLabel,
  transformedCategories
) => {
  const category = processTopNavItem(topNavItem, language, country, jumpToLabel)
  if (category) {
    transformedCategories?.push(category)
  }
}

/**
 * Validate menu data structure
 * @param {Object} menuData - Menu data to validate
 * @returns {boolean} Whether the data structure is valid
 */
const validateMenuData = menuData => {
  return !!(
    menuData &&
    typeof menuData === 'object' &&
    Array.isArray(menuData?.categories) &&
    menuData.categories?.length > 0
  )
}

/**
 * Get menu data from ContentStack API
 * @param {Object} contentStackData - ContentStack navigation data
 * @param {string} language - The language code (e.g., 'en')
 * @param {string} country - The country code (e.g., 'us')
 * @returns {Object} Menu data in expected format
 */
const getMenuData = (contentStackData, language = 'en', country = 'us') => {
  try {
    if (contentStackData && contentStackData?.top_navigation) {
      const transformedData = transformContentStackToMenuData(
        contentStackData,
        language,
        country
      )

      if (
        transformedData?.categories &&
        transformedData?.categories?.length > 0
      ) {
        return transformedData
      }
    }

    return { categories: [] }
  } catch (error) {
    console.error('Error getting menu data:', error)
    return { categories: [] }
  }
}

const marketSelectorData = contentStackData => {
  try {
    if (!contentStackData?.entry) {
      return {
        regions: [],
        markets: [],
        labels: {},
        use_full_language_name: false,
      }
    }
    const entry = contentStackData?.entry?.reference?.[0]

    const labels = {
      rememberMySelection: entry?.remember_my_selection || '',
      selectYourMarket: entry?.select_your_market || '',
      cancel: entry?.cancel || '',
    }
    const useFullLanguageName = entry?.use_full_language_name === true
    const regions = entry?.regions || []
    let markets = []
    try {
      markets = (entry?.regions || [])
        .map(region => {
          if (!region) return null

          return {
            region: region?.region_title || '',
            countries: (region?.markets || [])
              .map(market => {
                if (!market) return null

                return {
                  name: market?.title || '',
                  currency: market?.currency_code || '',
                  iso_code_2: market?.iso_code_2 || '',
                  default_language_code: market?.default_language_code || '',
                  languages: (market?.languages || [])
                    .map(lang => {
                      if (!lang) return null

                      return {
                        code: lang?.iso_code_2 || '',
                        full_name: lang?.full_name || '',
                        general_name: lang?.general_name || '',
                      }
                    })
                    .filter(Boolean),
                }
              })
              .filter(Boolean),
          }
        })
        .filter(Boolean)
    } catch (marketsError) {
      markets = []
    }

    const transformedData = {
      labels,
      regions,
      markets,
      use_full_language_name: useFullLanguageName,
    }

    return transformedData
  } catch (error) {
    return {
      markets: [],
      labels: {},
      regions: [],
      use_full_language_name: false,
    }
  }
}

export {
  transformCSPageEntries,
  transformContentStackToMenuData,
  transformSubCategories,
  transformCategory,
  transformLink,
  transformListsOfLinks,
  replaceUrlPlaceholders,
  validateMenuData,
  getMenuData,
  marketSelectorData,
}
